import React from 'react';
import { motion } from 'framer-motion';
import { PageHero } from '../components/PageHero';
import { SectionLabel, SectionHeading, PrimaryButton } from '../components/ui/UI';
import { SectionStage } from '../components/SectionStage';
const cases = [{
  title: 'Security control validation',
  desc: 'Confirm whether your existing controls detect, block, or miss real-world attacker behavior.',
  sting: 'You already paid for the tools. Periscan helps you find out what they\u2019re doing.'
}, {
  title: 'Detection engineering',
  desc: 'Test alerts, rules, workflows, and coverage against repeatable attack behaviors.',
  sting: 'Good detections should survive contact with reality.'
}, {
  title: 'Exposure reduction',
  desc: 'Prioritize what to fix based on validated impact, not just static severity scores.',
  sting: 'Critical is not always urgent. Reachable, exploitable, and ignored usually is.'
}, {
  title: 'Red and blue team collaboration',
  desc: 'Give offensive and defensive teams a shared validation loop: test, observe, fix, retest.',
  sting: 'Less finger-pointing. More signal.'
}, {
  title: 'Executive reporting',
  desc: 'Turn validation results into clear evidence of control effectiveness and risk reduction.',
  sting: 'Boards do not need more heat maps. They need proof.'
}, {
  title: 'Audit and compliance support',
  desc: 'Show that security controls are being tested continuously, not just documented optimistically once a year.',
  sting: '"Documented optimistically" is not a control.'
}, {
  title: 'Safe AI model adoption',
  desc: 'Allow your team to use powerful cyber AI models to reason over your environment without giving them direct network access.',
  sting: 'The model thinks. Periscan controls. Evidence proves.'
}];
export function UseCases() {
  return <>
      <PageHero eyebrow="Use Cases" title="Built for the work" titleMuted="security teams actually have to do." description="Periscan slots into the security workflows teams already run — and gives them better proof in every one." bg="none" data-id="element-1829" />

      <SectionStage fx="constellation" data-id="element-1830">
        <section className="relative w-full py-32 border-t border-white/5" data-id="element-1831">
          <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1832">
            <SectionLabel number="01" label="Use Cases" className="mb-8" data-id="element-1833" />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20" data-id="element-1834">
              <div className="lg:col-span-8" data-id="element-1835">
                <SectionHeading size="xl" data-id="element-1836">
                  Seven places teams put
                  <br data-id="element-1837" />
                  <span className="text-white/40" data-id="element-1838">Periscan to work first.</span>
                </SectionHeading>
              </div>
              <div className="lg:col-span-4 lg:pt-4" data-id="element-1839">
                <p className="text-base text-white/60 leading-relaxed" data-id="element-1840">
                  Different teams. Same outcome: a validated answer to the
                  question that actually matters.
                </p>
              </div>
            </div>

            <ol className="border-t border-white/5" data-id="element-1841">
              {cases.map((c, i) => <motion.li key={c.title} initial={{
              opacity: 0,
              y: 12
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-40px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.04
            }} className="border-b border-white/5 py-10 grid grid-cols-12 gap-6 items-baseline" data-id="element-1842">
                  <span className="col-span-12 md:col-span-1 font-mono text-sm text-[#1a8fd4]/70 tracking-[0.2em]" data-id="element-1843">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 className="col-span-12 md:col-span-4 text-2xl md:text-3xl text-white font-semibold tracking-tight leading-snug" data-id="element-1844">
                    {c.title}
                  </h3>
                  <div className="col-span-12 md:col-span-7 max-w-2xl" data-id="element-1845">
                    <p className="text-base md:text-lg text-white/65 leading-relaxed mb-3" data-id="element-1846">
                      {c.desc}
                    </p>
                    <p className="text-sm text-[#1a8fd4]/85 italic" data-id="element-1847">
                      {c.sting}
                    </p>
                  </div>
                </motion.li>)}
            </ol>

            <div className="mt-20 flex justify-start" data-id="element-1848">
              <PrimaryButton href="#" size="lg" data-id="element-1849">
                Coming Soon
              </PrimaryButton>
            </div>
          </div>
        </section>
      </SectionStage>
    </>;
}